/**
 * PlanBatchModal
 *
 * Action #3 in the SpeedDialFab — kicks off a content batch for a brand.
 * POSTs to /api/agency/portfolio/plan-batch which creates a notification for
 * the brand's lead with the post count + notes.
 *
 * Sections:
 * 1. Brand selection — low-pipeline brands sorted first
 * 2. Post count — number stepper, defaults to cadenceWeekly * 2
 * 3. Notes for creator — optional textarea
 */

import { usePortfolioLocks } from '@/hooks/usePortfolioLocks';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { showToast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { Textarea } from '@/components/ui/Textarea';
import { NumberStepper } from '@/components/ui/NumberStepper';
import { type PortfolioClient } from '@/data/portfolio.types';
import { usePortfolioClients } from '@/hooks/usePortfolio';
import { portfolioService } from '@/services/portfolio.service';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { avatarTextClass } from '@/utils/avatarContrast';
import { isLowPipeline } from '@/utils/lowPipeline';
import { lockBodyScroll } from '@/hooks/useBodyScrollLock';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PlanBatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Pre-select this brand on open (e.g. deep-linked from an agency-alert
   * email's "Plan a batch →" CTA). Falls back to the default batch-due /
   * first-brand logic when unset or when the id isn't in the loaded list.
   */
  initialClientId?: number | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Brand needs a new batch when its pipeline is low (server SSOT: less than 2 weeks of planned + ready content) */
function needsBatch(client: PortfolioClient): boolean {
  return isLowPipeline(client);
}

// ── Component ─────────────────────────────────────────────────────────────────

export const PlanBatchModal = ({ isOpen, onClose, initialClientId = null }: PlanBatchModalProps) => {
  const { t } = useTranslation('agency');
  const { isLocked } = usePortfolioLocks();

  // Live portfolio clients from the backend aggregation endpoint
  const { data: portfolioData } = usePortfolioClients();
  const portfolioClients = portfolioData?.clients ?? [];

  // Sort: batch-due brands first, then alphabetical - recomputed as live data arrives
  const sortedClients = useMemo(() => {
    return [...portfolioClients].sort((a, b) => {
      const aDue = needsBatch(a) ? 0 : 1;
      const bDue = needsBatch(b) ? 0 : 1;
      if (aDue !== bDue) return aDue - bDue;
      return a.name.localeCompare(b.name);
    });
  }, [portfolioClients]);

  // ── Form state ──
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [postCount, setPostCount] = useState(14);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Pre-select the deep-linked brand if one was passed in and is present in
  // the loaded list; otherwise fall back to the first batch-due brand.
  useEffect(() => {
    if (isOpen && sortedClients.length > 0) {
      const deepLinked =
        initialClientId !== null && sortedClients.some((c) => c.id === initialClientId)
          ? initialClientId
          : null;
      if (deepLinked !== null) {
        setSelectedClientId(deepLinked);
        return;
      }
      const firstDue = sortedClients.find(needsBatch);
      setSelectedClientId(firstDue?.id ?? sortedClients[0]?.id ?? null);
    }
  }, [isOpen, sortedClients, initialClientId]);

  // Update default post count when brand selection changes
  useEffect(() => {
    const client = portfolioClients.find((c) => c.id === selectedClientId);
    if (client) {
      const defaultCount = Math.max(4, client.cadenceWeekly * 2);
      setPostCount(defaultCount);
    }
  }, [selectedClientId, portfolioClients]);

  // ── ESC key ──
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) handleClose();
    };
    document.addEventListener('keydown', handler);
    // Lock background page scroll while the sheet is open so mobile only
    // shows the modal's own scrollbar, not two stacked ones.
    const unlockScroll = lockBodyScroll();
    return () => {
      document.removeEventListener('keydown', handler);
      unlockScroll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Reset & close ──
  const handleClose = () => {
    setSelectedClientId(null);
    setPostCount(14);
    setNotes('');
    setSaving(false);
    onClose();
  };

  // ── Submit ──
  const handleBrief = async () => {
    if (!selectedClientId) return;
    setSaving(true);
    try {
      await portfolioService.planBatch({
        teamId: selectedClientId,
        postCount,
        notes: notes.trim() || undefined,
      });
      showToast(
        t('fab_plan_batch_toast_success', { defaultValue: 'Batch brief sent' }),
        'success',
      );
      handleClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showToast(
        t('fab_plan_batch_toast_error', {
          defaultValue: 'Could not send brief: {{msg}}',
          msg,
        }),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  const canBrief = selectedClientId !== null;

  // Unsaved-work guard: dirty while open and the creator notes (the only
  // free-text field — brand/count are pre-populated selections) have content.
  useUnsavedGuard(isOpen && notes.trim().length > 0, 'plan-batch-modal');

  if (!isOpen) return null;

  return (
    <div
      className="fixed left-0 right-0 top-0 bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:inset-0 z-50 flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="plan-batch-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className="
          relative bg-white flex flex-col shadow-2xl
          w-full h-full rounded-t-2xl rounded-b-none
          sm:h-auto sm:w-[calc(100vw-2rem)] sm:max-w-xl sm:mx-0 sm:rounded-2xl
          overflow-hidden max-h-[90dvh]
        "
      >
        {/* Drag handle (mobile) */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" aria-hidden="true" />
        </div>

        {/* ── Sticky Header ── */}
        <div className="relative shrink-0 border-b border-gray-900/10 pl-5 pr-14 py-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-medium mb-1">
            {t('fab_plan_batch_overline', { defaultValue: 'Plan content batch' })}
          </p>
          <div>
            <h2
              id="plan-batch-title"
              className="font-wagner text-2xl leading-tight tracking-tight text-gray-900"
            >
              {t('fab_plan_batch_title', { defaultValue: 'New content batch' })}
            </h2>
            <button
              type="button"
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors outline-none border border-transparent focus-visible:border-primary-600"
              onClick={handleClose}
              aria-label={t('modal_close', { defaultValue: 'Close' })}
            >
              <i className="hm hm-cross" style={{ fontSize: 15 }} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* ── Scrollable Body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-5 divide-y divide-gray-100 space-y-0 [&>section]:pt-5 [&>section:first-child]:pt-0">

          {/* 1. Brand selection */}
          <section aria-labelledby="section-brand-select">
            <p
              id="section-brand-select"
              className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
            >
              {t('fab_plan_batch_brand_label', { defaultValue: 'For which brand?' })}
              <span className="text-coral-500 ml-0.5" aria-hidden="true">*</span>
            </p>
            <div
              className="rounded-xl border border-gray-200 overflow-hidden max-h-64 overflow-y-auto divide-y divide-gray-50"
              role="listbox"
              aria-label={t('fab_plan_batch_brand_label', { defaultValue: 'Select brand' })}
            >
              {sortedClients.map((client) => {
                const isSelected = selectedClientId === client.id;
                const isBatchDue = needsBatch(client);
                return (
                  <button
                    key={client.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => setSelectedClientId(client.id)}
                    className={`
                      w-full text-left flex items-center gap-3 pl-3.5 pr-4 py-2.5 transition-colors
                      focus:outline-none border-l-2 border-transparent focus-visible:border-primary-600 focus-visible:bg-primary-50
                      ${isSelected ? 'bg-primary-25' : 'hover:bg-gray-50'}
                    `}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${avatarTextClass(client.avatarColor)} text-xs shrink-0 ${client.avatarColor}`}
                      aria-hidden="true"
                    >
                      {client.avatarInitial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {client.name}
                      </p>
                      <p className="text-xs text-gray-400">
                        {!isLocked('portfolio_cadence') && <>{client.cadenceWeekly}×/wk · </>}{!isLocked('portfolio_runway') && t('runway_bar_days_short', { days: client.coverDays })}
                      </p>
                    </div>
                    {/* Batch due badge */}
                    {isBatchDue && (
                      <span
                        className="flex items-center justify-center w-5 h-5 rounded-full bg-coral-500 text-coral-contrast text-[9px] font-bold shrink-0"
                        title={t('low_pipeline_short', { defaultValue: 'Low pipeline' })}
                        aria-label={t('low_pipeline_short', { defaultValue: 'Low pipeline' })}
                      >
                        !
                      </span>
                    )}
                    {/* Selected checkmark */}
                    {isSelected && (
                      <i
                        className="hm hm-tick-circle text-primary-500 shrink-0"
                        style={{ fontSize: 16 }}
                        aria-hidden="true"
                      />
                    )}
                  </button>
                );
              })}
            </div>
            {sortedClients.some(needsBatch) && (
              <p className="text-[10px] text-coral-600 mt-1.5 flex items-center gap-1">
                <span
                  className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-coral-500 text-coral-contrast text-[8px] font-bold shrink-0"
                  aria-hidden="true"
                >
                  !
                </span>
                {t('fab_plan_batch_low_pipeline_hint', { defaultValue: 'Highlighted brands have a low pipeline: less than 2 weeks of planned and ready content.' })}
              </p>
            )}
          </section>

          {/* 2. Post count */}
          <section aria-labelledby="section-post-count">
            <p
              id="section-post-count"
              className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
            >
              {t('fab_plan_batch_count_label', { defaultValue: 'How many posts?' })}
            </p>
            <div className="flex items-center gap-3">
              <NumberStepper
                value={postCount}
                onChange={setPostCount}
                min={1}
                max={99}
                size="md"
              />
              <span className="text-sm text-gray-500">
                {t('fab_plan_batch_count_hint', { defaultValue: 'posts (2-week batch default)' })}
              </span>
            </div>
          </section>

          {/* 3. Creator notes */}
          <section aria-labelledby="section-creator-notes" className="pb-2">
            <p
              id="section-creator-notes"
              className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
            >
              {t('fab_plan_batch_notes_label', { defaultValue: 'Notes for the creator' })}
              <span className="text-gray-300 ml-1 text-[10px] font-normal normal-case">
                {t('status_compose_optional', { defaultValue: 'optional' })}
              </span>
            </p>
            <Textarea
              id="batch-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('fab_plan_batch_notes_placeholder', {
                defaultValue: 'Tone, campaign theme, do\'s and don\'ts, deadlines…',
              })}
              rows={3}
              className="resize-none"
            />
          </section>
        </div>

        {/* ── Sticky Footer ── */}
        <div className="shrink-0 border-t border-gray-100 bg-white px-5 py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
          <div className="flex items-center gap-2 justify-end">
            <CancelButton onClick={handleClose} tooltip={t('fab_cancel', { defaultValue: 'Cancel' })} />
            <Button
              variant="primary"
              size="md"
              disabled={!canBrief}
              loading={saving}
              onClick={() => { void handleBrief(); }}
            >
              <i className="hm hm-paper-plane mr-1.5" style={{ fontSize: 14 }} aria-hidden="true" />
              {t('fab_plan_batch_submit', { defaultValue: 'Brief the creator' })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanBatchModal;
